import { useEffect, useRef, useState } from "react";
import { Page } from "@iq/models";
import useUserStore from "../states/user";
import { createDiscount, deleteDiscount, fetchDiscounts, updateDiscount } from "../services/storeApi";
import "../styles/discount.css";

const emptyDraft = { name: "", code: "", percentage: "", expires_at: "", max_usage: "" };

function localDateTime(timestamp) {
	const date = new Date(timestamp);
	const pad = (value) => String(value).padStart(2, "0");
	return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`;
}

function DiscountManager() {
	const [discounts, setDiscounts] = useState([]);
	const [draft, setDraft] = useState(emptyDraft);
	const [editing, setEditing] = useState(null);
	const [loading, setLoading] = useState(true);
	const [saving, setSaving] = useState(false);
	const [loadError, setLoadError] = useState("");
	const [error, setError] = useState("");
	const [message, setMessage] = useState("");
	const [refresh, setRefresh] = useState(0);
	const busyRef = useRef(false);
	const mountedRef = useRef(false);
	const nameRef = useRef(null);

	useEffect(() => {
		mountedRef.current = true;
		return () => { mountedRef.current = false; };
	}, []);

	useEffect(() => {
		let active = true;
		setLoading(true);
		setLoadError("");
		fetchDiscounts().then((items) => {
			if (active) setDiscounts(items);
		}).catch((err) => {
			if (active) setLoadError(err.message || "Failed to load discounts.");
		}).finally(() => {
			if (active) setLoading(false);
		});
		return () => { active = false; };
	}, [refresh]);

	const unavailable = loading || saving || !!loadError;

	function resetEditor() {
		setEditing(null);
		setDraft(emptyDraft);
		setError("");
	}

	async function mutate(action, successMessage) {
		if (busyRef.current || unavailable) return;
		busyRef.current = true;
		setSaving(true);
		setError("");
		setMessage("");
		try {
			await action();
			if (!mountedRef.current) return;
			resetEditor();
			setMessage(successMessage);
			setLoading(true);
			setRefresh((value) => value + 1);
		} catch (err) {
			if (mountedRef.current) setError(err.message || "Failed to save discount.");
		} finally {
			busyRef.current = false;
			if (mountedRef.current) setSaving(false);
		}
	}

	function handleSubmit(event) {
		event.preventDefault();
		if (busyRef.current || unavailable) return;
		const name = draft.name.trim();
		const code = draft.code.trim();
		const percentage = Number(draft.percentage);
		const maxUsage = Number(draft.max_usage);
		const expiry = new Date(draft.expires_at);
		if (!name || Array.from(name).length > 255 || (!editing && (!code || Array.from(code).length > 100))) {
			setError("Enter a name (1-255 characters) and a code (1-100 characters).");
			return;
		}
		if (!/^\d+$/.test(draft.percentage) || percentage < 1 || percentage > 100) {
			setError("Percentage must be a whole number between 1 and 100.");
			return;
		}
		if (!/^\d+$/.test(draft.max_usage) || !Number.isSafeInteger(maxUsage) || maxUsage < 1 || maxUsage < (editing?.usage_count || 0)) {
			setError("Maximum uses must be a positive whole number, not below the current usage count.");
			return;
		}
		if (!draft.expires_at || !Number.isFinite(expiry.getTime())) {
			setError("Enter a valid expiry date and time.");
			return;
		}
		// Preserve the exact stored expiry if the local date field was not edited.
		const expiresAt = editing && draft.expires_at === localDateTime(editing.expires_at)
			? editing.expires_at : expiry.toISOString();
		const details = { name, percentage, expires_at: expiresAt, max_usage: maxUsage };
		mutate(
			() => editing
				? updateDiscount({ ...details, discount_id: editing.discount_id })
				: createDiscount({ ...details, code }),
			editing ? "Discount updated." : "Discount created.",
		);
	}

	function startEditing(discount) {
		setEditing(discount);
		setDraft({
			name: discount.name, code: discount.code, percentage: String(discount.percentage),
			expires_at: localDateTime(discount.expires_at), max_usage: String(discount.max_usage),
		});
		setError("");
		setMessage("");
		nameRef.current?.focus();
	}

	function handleDelete(discount) {
		if (busyRef.current || unavailable) return;
		if (!window.confirm(`Delete discount "${discount.name}" (${discount.code})? This also removes it from linked services.`)) return;
		mutate(() => deleteDiscount(discount.discount_id), "Discount deleted.");
	}

	function field(name) {
		return {
			id: `discount-${name}`, value: draft[name],
			onChange: (event) => setDraft((current) => ({ ...current, [name]: event.target.value })),
			disabled: unavailable,
		};
	}

	return (
		<div className="discountPage">
			<header>
				<h2>Manage discounts</h2>
				<p>Create booking discount codes and set the total number of uses available.</p>
			</header>
			{message && <p role="status">{message}</p>}
			{error && <p className="discountError" role="alert">{error}</p>}
			{loadError && <div className="discountError" role="alert">
				<p>{loadError} Retry loading before making more changes.</p>
				<button className="btn" onClick={() => { setLoading(true); setRefresh((value) => value + 1); }}>Retry</button>
			</div>}
			<form className="discountEditor" onSubmit={handleSubmit} noValidate>
				<h3>{editing ? "Edit discount" : "Create discount"}</h3>
				<div className="discountFields">
					<label htmlFor="discount-name">Name<input ref={nameRef} {...field("name")} maxLength={255} /></label>
					<label htmlFor="discount-code">Code<input {...field("code")} disabled={unavailable || !!editing} maxLength={100} /></label>
					<label htmlFor="discount-percentage">Percentage (%)<input {...field("percentage")} type="number" min="1" max="100" step="1" /></label>
					<label htmlFor="discount-expires_at">Expires at (local time)<input {...field("expires_at")} type="datetime-local" step="1" /></label>
					<label htmlFor="discount-max_usage">Maximum uses<input {...field("max_usage")} type="number" min={Math.max(1, editing?.usage_count || 0)} step="1" /></label>
				</div>
				{editing && <p>Code cannot be changed. Already used {editing.usage_count} times.</p>}
				<p>For example, 10 maximum uses makes the code available for ten total redemptions.</p>
				<div className="discountActions">
					<button className="btn discountPrimary" disabled={unavailable} type="submit">{saving ? "Saving..." : editing ? "Save changes" : "Create discount"}</button>
					{editing && <button className="btn" type="button" disabled={saving} onClick={resetEditor}>Cancel</button>}
				</div>
			</form>
			<section aria-label="Discounts" aria-busy={loading}>
				{loading && <p role="status">Loading discounts...</p>}
				{!loading && !loadError && discounts.length === 0 && <p>No discounts yet. Create your first discount above.</p>}
				<div className="discountTableScroll">
					<table className="discountTable">
						<caption>Saved discounts</caption>
						<thead><tr>{["Name", "Code", "Discount", "Usage", "Expires", "Status", "Actions"].map((heading) => <th key={heading} scope="col">{heading}</th>)}</tr></thead>
						<tbody>{discounts.map((discount) => (
							<tr key={discount.discount_id}>
								<td>{discount.name}</td><td><code>{discount.code}</code></td><td>{discount.percentage}%</td>
								<td>{discount.usage_count} / {discount.max_usage}</td>
								<td>{new Date(discount.expires_at).toLocaleString()}</td>
								<td>{Date.parse(discount.expires_at) <= Date.now() ? "Expired" : discount.usage_count >= discount.max_usage ? "Fully used" : "Available"}</td>
								<td><div className="discountActions">
									<button className="btn" disabled={unavailable || !!editing} onClick={() => startEditing(discount)} aria-label={`Edit discount ${discount.code}`}>Edit</button>
									<button className="btn discountDelete" disabled={unavailable || !!editing} onClick={() => handleDelete(discount)} aria-label={`Delete discount ${discount.code}`}>Delete</button>
								</div></td>
							</tr>
						))}</tbody>
					</table>
				</div>
			</section>
		</div>
	);
}

function Discount() {
	const organizationId = useUserStore((state) => state.session?.organization_id);
	return <Page>{organizationId
		? <DiscountManager key={organizationId} />
		: <p role="status">Waiting for your organization session...</p>}
	</Page>;
}

export default Discount;